import { getIcon, type IconPath } from "../assets/icons";
import type { CustomIcon, IconPattern, LogoState, TitleStyle3D } from "../state/logoStore";
import { ensureContrast, relativeLuminance } from "./contrast";
import { buildIconPatternShapes } from "./patterns";
import { CONTAINER_SHAPES } from "./containerShapes";

/**
 * Pick a soft halo color for text drawn over `surface` — used as a gentle
 * shadow-style outline when the user explicitly opts in via the slider.
 * Semi-transparent dark on light surfaces, semi-transparent light on dark.
 */
function pickHaloColor(surface: string): string {
  return relativeLuminance(surface) > 0.5 ? "rgba(0,0,0,0.28)" : "rgba(255,255,255,0.32)";
}

export interface RenderOptions {
  width?: number;
  height?: number;
  pad?: number;
  /** Optional list of user-uploaded SVG icons so renderer can find them. */
  customIcons?: CustomIcon[];
  /** Pre-built QR code paths (already normalised to 0..100 viewBox). */
  qrPaths?: string[];
  /**
   * Auto-correct title/slogan/icon colors so they meet WCAG contrast against
   * the background (text 4.5:1, icon 3:1). Default true. Set false to render
   * exactly the colors in state (e.g., for color-picker UI swatches).
   */
  enforceContrast?: boolean;
}

const escapeXml = (str: string) =>
  str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Mix a hex color toward black by `amount` (0..1). amount=0 → unchanged, amount=1 → black. */
function darkenHex(hex: string, amount: number): string {
  const m = /^#?([\da-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const t = 1 - Math.max(0, Math.min(1, amount));
  const r = Math.round(((n >> 16) & 0xff) * t);
  const g = Math.round(((n >> 8) & 0xff) * t);
  const b = Math.round((n & 0xff) * t);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

export interface TitleBlock { defs: string; body: string; }


const CHROME_STOPS = `
  <stop offset="0%" stop-color="#3a3a3a"/>
  <stop offset="22%" stop-color="#dddddd"/>
  <stop offset="46%" stop-color="#ffffff"/>
  <stop offset="50%" stop-color="#f4f4f4"/>
  <stop offset="54%" stop-color="#ffffff"/>
  <stop offset="78%" stop-color="#7a7a7a"/>
  <stop offset="100%" stop-color="#1a1a1a"/>`;

const GOLD_STOPS = `
  <stop offset="0%" stop-color="#4a2400"/>
  <stop offset="22%" stop-color="#d49b3a"/>
  <stop offset="46%" stop-color="#ffe07a"/>
  <stop offset="50%" stop-color="#fff3b8"/>
  <stop offset="54%" stop-color="#ffe07a"/>
  <stop offset="78%" stop-color="#a06a18"/>
  <stop offset="100%" stop-color="#2a1300"/>`;

function bevelFilter(id: string): string {
  return `<filter id="${id}" x="-10%" y="-10%" width="120%" height="120%">
  <feGaussianBlur in="SourceAlpha" stdDeviation="1.5" result="blur"/>
  <feSpecularLighting in="blur" surfaceScale="4" specularConstant="1.4" specularExponent="22" lighting-color="white" result="spec">
    <feDistantLight azimuth="225" elevation="55"/>
  </feSpecularLighting>
  <feComposite in="spec" in2="SourceAlpha" operator="in" result="specCut"/>
  <feMerge>
    <feMergeNode in="SourceGraphic"/>
    <feMergeNode in="specCut"/>
  </feMerge>
</filter>`;
}

/** Build the inner contents of the title group at local origin (0, 0). */
export function buildTitleBlock(args: {
  text: string; anchor: "start" | "middle" | "end";
  fontFamily: string; fontSize: number; color: string;
  style3D: TitleStyle3D; depth: number; stroke: number;
  fitAttr: string; uid: string;
  /** Outline colour used as a legibility halo around text (auto-contrast). */
  haloColor?: string;
}): TitleBlock {
  const { text, anchor, fontFamily, fontSize, color, style3D, depth, stroke, fitAttr, uid } = args;
  const haloColor = args.haloColor ?? "#111111";
  const escaped = escapeXml(text);
  const baseAttrs = `text-anchor="${anchor}" font-family="${fontFamily}, sans-serif" font-size="${fontSize}" font-weight="900"${fitAttr}`;
  // A thick contrasting outline drawn UNDER the colored fill — the universal
  // legibility fix recommended by the user. Active whenever `stroke > 0`.
  const haloLayer = (sw: number) => sw > 0
    ? `<text x="0" y="0" ${baseAttrs} fill="none" stroke="${haloColor}" stroke-width="${sw}" stroke-linejoin="round" stroke-linecap="round">${escaped}</text>`
    : "";

  // Helper: stacked depth layers (back-to-front), darkened shade over `color`.
  const stackLayers = (d: number, darkStart: number, darkEnd: number): string[] => {
    const layers: string[] = [];
    const n = Math.max(1, Math.round(d));
    for (let i = n; i >= 1; i--) {
      const t = i / n;
      const shade = darkenHex(color, darkStart + (darkEnd - darkStart) * t);
      layers.push(`<text x="${i * 0.7}" y="${i * 0.7}" ${baseAttrs} fill="${shade}">${escaped}</text>`);
    }
    return layers;
  };

  if (style3D === "flat") {
    const sw = Math.max(0, stroke);
    return {
      defs: "",
      body: haloLayer(sw) + `<text x="0" y="0" ${baseAttrs} fill="${color}">${escaped}</text>`,
    };
  }

  if (style3D === "stacked") {
    const sw = Math.max(0, stroke);
    const layers = stackLayers(depth, 0.4, 0.85);
    // Halo sits at the very back so it surrounds both the depth shade and
    // the front fill — gives a clean readable outline on any background.
    const body = haloLayer(sw) + layers.join("") + `<text x="0" y="0" ${baseAttrs} fill="${color}">${escaped}</text>`;
    return { defs: "", body };
  }

  if (style3D === "outline") {
    const sw = Math.max(1, stroke);
    return {
      defs: "",
      body: `<text x="0" y="0" ${baseAttrs} fill="none" stroke="${color}" stroke-width="${sw}" stroke-linejoin="round" stroke-linecap="round">${escaped}</text>`,
    };
  }

  if (style3D === "neon") {
    const filtId = `lp-neon-${uid}`;
    const defs = `<filter id="${filtId}" x="-50%" y="-50%" width="200%" height="200%">
  <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="b1"/>
  <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="b2"/>
  <feGaussianBlur in="SourceGraphic" stdDeviation="14" result="b3"/>
  <feMerge>
    <feMergeNode in="b3"/>
    <feMergeNode in="b2"/>
    <feMergeNode in="b1"/>
    <feMergeNode in="SourceGraphic"/>
  </feMerge>
</filter>`;
    const coreStroke = Math.max(1, Math.round(fontSize * 0.035));
    return {
      defs,
      body: `<g filter="url(#${filtId})"><text x="0" y="0" ${baseAttrs} fill="#ffffff" stroke="${color}" stroke-width="${coreStroke}" paint-order="stroke fill">${escaped}</text></g>`,
    };
  }

  if (style3D === "glitch") {
    const off = Math.max(2, Math.round(fontSize * 0.04));
    const sw = Math.max(0, stroke);
    return {
      defs: "",
      body: [
        haloLayer(sw),
        `<text x="${-off}" y="0" ${baseAttrs} fill="#00e7ff" opacity="0.85">${escaped}</text>`,
        `<text x="${off}" y="0" ${baseAttrs} fill="#ff2bd6" opacity="0.85">${escaped}</text>`,
        `<text x="0" y="0" ${baseAttrs} fill="${color}">${escaped}</text>`,
      ].join(""),
    };
  }

  if (style3D === "comic") {
    // Bold uppercase comic lettering — black stroke + dual hard offset shadow.
    // First shadow is solid black, second is a darker shade of the title color
    // pushed further out, matching the classic Marvel/Beano cover treatment.
    const off1 = Math.max(2, Math.round(fontSize * 0.05));
    const off2 = Math.max(off1 + 2, Math.round(fontSize * 0.09));
    const sw = Math.max(2, stroke || Math.round(fontSize * 0.04));
    const shadow2 = darkenHex(color, 0.4);
    return {
      defs: "",
      body: [
        `<text x="${off2}" y="${off2}" ${baseAttrs} fill="${shadow2}">${escaped}</text>`,
        `<text x="${off1}" y="${off1}" ${baseAttrs} fill="#111111">${escaped}</text>`,
        `<text x="0" y="0" ${baseAttrs} fill="${color}" stroke="#111111" stroke-width="${sw}" stroke-linejoin="round" paint-order="stroke fill">${escaped}</text>`,
      ].join(""),
    };
  }

  // chrome / gold / extruded share metallic gradient + bevel filter
  const gradId = `lp-grad-${uid}`;
  const filtId = `lp-bevel-${uid}`;
  const isGold = style3D === "gold";
  const stops = isGold ? GOLD_STOPS : CHROME_STOPS;
  const defs = `<linearGradient id="${gradId}" x1="0" y1="0" x2="0" y2="1">${stops}
</linearGradient>
${bevelFilter(filtId)}`;

  const sw = Math.max(0, stroke);
  // Use the auto-contrast halo colour for the metallic outline — black on
  // light surfaces, white on dark — so the gradient never blends into the
  // background. Previously this used `color` (the title fill) which gave
  // a same-colour outline that vanished on similar-coloured surfaces.
  const strokeAttr = sw > 0 ? ` stroke="${haloColor}" stroke-width="${sw}" paint-order="stroke fill"` : "";
  const faceAttrs = `fill="url(#${gradId})"${strokeAttr} filter="url(#${filtId})"`;

  if (style3D === "chrome" || style3D === "gold") {
    return {
      defs,
      body: `<text x="0" y="0" ${baseAttrs} ${faceAttrs}>${escaped}</text>`,
    };
  }

  // extruded — darkened titleColor stack behind a metallic face
  const layers = stackLayers(depth, 0.55, 0.9);
  layers.push(`<text x="0" y="0" ${baseAttrs} ${faceAttrs}>${escaped}</text>`);
  return { defs, body: layers.join("") };
}

/** Build the live logo as an SVG string. */
export function renderLogoSVG(s: Pick<LogoState,
  "title" | "slogan" | "iconId" | "titleFont" | "sloganFont" |
  "titleColor" | "sloganColor" | "iconColor" | "background" |
  "layout" | "titleSize" | "sloganSize" | "iconScale"> & Partial<Pick<LogoState,
    "iconAccent" |
    "logoFrame" | "logoFrameColor" | "logoFrameSize" |
    "logoFrameWidth" | "logoFrameHeight" |
    "logoFrameBorderColor" | "logoFrameBorderWidth" | "logoFrameBorderStyle" |
    "containerShape" | "containerColor" |
    "containerColorOpacity" | "containerGradient" | "containerColor2" | "containerGradientAngle" |
    "containerPattern" | "containerPatternColor" | "containerPatternDensity" |
    "iconPattern" | "iconPatternColor" | "iconPatternDensity" |
    "titleStyle3D" | "titleDepth3D" | "titleStroke3D" | "titleSkew" |
    "sloganStyle3D" | "sloganDepth3D" | "sloganStroke3D" | "sloganSkew" |
    "titleSloganGap" | "sloganOffsetX" | "iconTextGap" |
    "titleOffsetX" | "titleOffsetY">>,
  opts: RenderOptions = {}
): string {
  const w = opts.width ?? 800;
  const h = opts.height ?? 800;
  const pad = opts.pad ?? 60;

  // Auto-correct foreground colors against the background so previews stay
  // readable regardless of source (image extraction, idea generator, user pick).
  // Text needs 4.5:1 (WCAG AA), icons treated as large UI: 3:1.
  // QR codes need a higher floor (~5:1) so scanners read reliably.
  // For transparent bg we assume the logo will sit on a white surface — common
  // viewing case for product mockups, web pages, etc.
  const enforce = opts.enforceContrast !== false;
  // Canvas background is always transparent — backdrop is provided by the
  // optional Logo Frame instead. The s.background field is kept for legacy
  // template/idea data but has no visual effect on the canvas.
  const bg = "transparent";
  const isTransparent = true;
  // If the user has a logo frame set, content sits on top of the frame surface,
  // so contrast should be computed against the frame color, not the canvas bg.
  const surface =
    (s.logoFrame && s.logoFrame !== "none" && s.logoFrameColor)
      ? s.logoFrameColor
      : (isTransparent ? "#ffffff" : bg);
  const isQrIcon = s.iconId === "__qr__";
  const iconMinRatio = isQrIcon ? 5.0 : 3.0;
  // Comic title style carries its own black stroke for legibility, so the
  // contrast checker would just fight the colour intent. Skip enforcement
  // for comic titles only — slogans still get checked.
  const titleEnforce = enforce && s.titleStyle3D !== "comic";
  const titleColor  = titleEnforce ? ensureContrast(s.titleColor,  surface, 4.5)         : s.titleColor;
  const sloganColor = enforce ? ensureContrast(s.sloganColor, surface, 4.5)            : s.sloganColor;
  const iconColor   = enforce ? ensureContrast(s.iconColor,   surface, iconMinRatio)   : s.iconColor;

  // Resolve icon: standard library, custom user icon, raster cartoon, or QR.
  // Library icons may have per-path fills (object form). QR + custom are always
  // single-color string arrays. Cartoons are raster PNGs rendered via <image>.
  let iconPaths: IconPath[] = [];
  let iconVB = { x: 0, y: 0, w: 100, h: 100 };
  let iconFillRule: "nonzero" | "evenodd" | undefined;
  let iconPngUrl: string | undefined;
  if (s.iconId === "__qr__" && opts.qrPaths) {
    iconPaths = opts.qrPaths;
  } else if (s.iconId && s.iconId.startsWith("custom_") && opts.customIcons) {
    const c = opts.customIcons.find((x) => x.id === s.iconId);
    if (c) { iconPaths = c.paths; iconVB = c.vb; }
  } else {
    const icon = getIcon(s.iconId);
    if (icon) {
      iconPaths = icon.paths;
      iconFillRule = icon.fillRule;
      iconPngUrl = icon.pngUrl;
    }
  }
  const hasIcon = iconPaths.length > 0 || !!iconPngUrl;
  const iconAccent = s.iconAccent ?? "#ff8aa3";

  /** Resolve a per-path fill token to an actual color string. */
  function resolveIconFill(fill: string | undefined): string {
    if (!fill || fill === "primary") return iconColor;
    if (fill === "accent") return iconAccent;
    return fill; // literal hex / named color
  }
  /** True when at least one icon path has a non-primary fill (multi-color icon). */
  const iconIsMultiColor = iconPaths.some(
    (p) => typeof p !== "string" && p.fill !== undefined && p.fill !== "primary"
  );

  const escape = escapeXml;
  const layout = s.layout;
  const showIcon = hasIcon && layout !== "text-only";
  const showText = layout !== "icon-only";

  // Compute positions per layout
  let iconX = 0, iconY = 0, iconSize = w * 0.25 * s.iconScale;
  let titleX = w / 2, titleY = h / 2, titleAnchor: "middle" | "start" | "end" = "middle";
  let sloganX = w / 2, sloganY = h / 2 + s.titleSize, sloganAnchor: "middle" | "start" | "end" = "middle";

  // Composition is centered as a group inside the canvas.
  const titleH = showText && s.title  ? s.titleSize  : 0;
  const sloganH = showText && s.slogan ? s.sloganSize : 0;
  // User-controllable distance between icon and text block. Default 24 matches
  // the previous hardcoded gap. Negative pulls icon onto text (creative
  // overlap); positive pushes them further apart.
  const userIconTextGap = typeof s.iconTextGap === "number" ? s.iconTextGap : 24;
  const gapIconText = (showIcon && (titleH || sloganH))
    ? Math.max(-50, Math.min(300, userIconTextGap))
    : 0;
  // User-controllable spacing between title and slogan. Default 12 matches the
  // pre-spacing-system layout. Negative pulls them onto each other; positive
  // pushes them apart (fixes "name covering slogan" cases).
  const userGap = typeof s.titleSloganGap === "number" ? s.titleSloganGap : 12;
  const gapTitleSlogan = (titleH && sloganH) ? Math.max(-50, Math.min(200, userGap)) : 0;
  const sloganNudgeX = Math.max(-200, Math.min(200, typeof s.sloganOffsetX === "number" ? s.sloganOffsetX : 0));
  const titleNudgeX = Math.max(-200, Math.min(200, typeof s.titleOffsetX === "number" ? s.titleOffsetX : 0));
  const titleNudgeY = Math.max(-200, Math.min(200, typeof s.titleOffsetY === "number" ? s.titleOffsetY : 0));

  // Compositions are vertically centered in the canvas.
  if (layout === "icon-top") {
    const iconBlockH = showIcon ? iconSize : 0;
    const textBlockH = titleH + gapTitleSlogan + sloganH;
    const blockH = iconBlockH + gapIconText + textBlockH;
    const blockTop = (h - blockH) / 2;
    iconX = (w - iconSize) / 2;
    iconY = blockTop;
    const textStartY = blockTop + iconBlockH + gapIconText;
    titleY = textStartY + titleH * 0.85;
    sloganY = titleY + gapTitleSlogan + sloganH * 0.95;
  } else if (layout === "icon-left" || layout === "icon-right") {
    // Compute the combined block width (icon + gap + longest text) and center it
    const naturalTitleW = (s.title || "").length * titleH * 0.55;
    const naturalSloganW = (s.slogan || "").length * sloganH * 0.55;
    const widestText = Math.max(naturalTitleW, naturalSloganW);
    // For icon-left / icon-right the same user-controllable gap drives the
    // horizontal distance between icon and text.
    const gapIconTextH = showIcon && (titleH || sloganH) ? gapIconText : 0;
    const maxBlockW = w - 2 * pad;
    const desiredBlockW = (showIcon ? iconSize : 0) + gapIconTextH + widestText;
    const blockW = Math.min(desiredBlockW, maxBlockW);
    const blockX = (w - blockW) / 2;
    const textBlockH = titleH + gapTitleSlogan + sloganH;
    const blockH = Math.max(showIcon ? iconSize : 0, textBlockH);
    const blockTop = (h - blockH) / 2;

    if (layout === "icon-left") {
      iconX = blockX;
      iconY = blockTop + (blockH - iconSize) / 2;
      titleX = blockX + (showIcon ? iconSize + gapIconTextH : 0);
      sloganX = titleX;
      titleAnchor = "start"; sloganAnchor = "start";
    } else {
      iconX = blockX + blockW - iconSize;
      iconY = blockTop + (blockH - iconSize) / 2;
      titleX = blockX + blockW - (showIcon ? iconSize + gapIconTextH : 0);
      sloganX = titleX;
      titleAnchor = "end"; sloganAnchor = "end";
    }
    const textStartY = blockTop + (blockH - textBlockH) / 2;
    titleY = textStartY + titleH * 0.85;
    sloganY = titleY + gapTitleSlogan + sloganH * 0.95;
  } else if (layout === "text-only") {
    const textBlockH = titleH + gapTitleSlogan + sloganH;
    const blockTop = (h - textBlockH) / 2;
    titleX = w / 2;
    titleY = blockTop + titleH * 0.85;
    sloganY = titleY + gapTitleSlogan + sloganH * 0.95;
  } else if (layout === "icon-only") {
    iconSize = w * 0.5 * s.iconScale;
    iconX = (w - iconSize) / 2;
    iconY = (h - iconSize) / 2;
  }

  // Container behind the icon (circle / square / rounded)
  const containerShape = s.containerShape || "none";
  const containerColor = s.containerColor || "#e0e7ff";
  const containerColor2 = s.containerColor2 || "#a5b4fc";
  const containerOpacity = Math.max(0, Math.min(100, typeof s.containerColorOpacity === "number" ? s.containerColorOpacity : 100)) / 100;
  const containerGradient = s.containerGradient ?? "none";
  const containerGradientAngle = ((s.containerGradientAngle ?? 135) % 360 + 360) % 360;
  const containerPattern: IconPattern = s.containerPattern ?? "none";
  // If user hasn't set a pattern color, fall back to bg (AT&T-style cutout). When
  // the bg is transparent, fall back to white so the pattern is still visible.
  const containerPatternColor = s.containerPatternColor ?? (isTransparent ? "#ffffff" : bg);
  const padFactor = 0.18;
  const containerSize = iconSize * (1 + padFactor * 2);
  const containerX = iconX + iconSize / 2 - containerSize / 2;
  const containerY = iconY + iconSize / 2 - containerSize / 2;
  const containerCx = iconX + iconSize / 2;
  const containerCy = iconY + iconSize / 2;
  const containerR = containerSize / 2;
  const containerRx = containerSize * 0.18;
  let containerSvg = "";
  let containerDefs = "";
  if (showIcon && containerShape !== "none") {
    // For object shapes we scale up the container box so the icon doesn't dominate
    // the body of the shape (e.g. so an icon fits inside the phone's "screen" area).
    const objectShape = CONTAINER_SHAPES[containerShape];
    // Object shapes (Phone, Cupcake, Lightbulb…) have NARROW bodies — Phone is
    // only 44% of its viewBox width — so a 1.5× scale leaves the icon overflowing
    // the sides and hiding the container pattern (stripes/dots/etc) behind it.
    // Scaling to 2.4× ensures even the narrowest shape comfortably wraps the icon.
    const objectScale = objectShape ? 2.4 : 1;
    const cSize = containerSize * objectScale;
    const cX = iconX + iconSize / 2 - cSize / 2;
    const cY = iconY + iconSize / 2 - cSize / 2;
    let shapeSvg = "";
    let fillRule = "";
    if (containerShape === "circle") {
      shapeSvg = `<circle cx="${containerCx}" cy="${containerCy}" r="${containerR}"/>`;
    } else if (containerShape === "square") {
      shapeSvg = `<rect x="${containerX}" y="${containerY}" width="${containerSize}" height="${containerSize}"/>`;
    } else if (containerShape === "rounded") {
      shapeSvg = `<rect x="${containerX}" y="${containerY}" width="${containerSize}" height="${containerSize}" rx="${containerRx}" ry="${containerRx}"/>`;
    } else if (objectShape) {
      const scale = cSize / 100;
      shapeSvg = `<g transform="translate(${cX} ${cY}) scale(${scale})">${objectShape.body}</g>`;
      if (objectShape.fillRule) fillRule = ` fill-rule="${objectShape.fillRule}"`;
    }

    // Resolve the container "paint" — either a flat colour with optional opacity,
    // or a linear/radial gradient between containerColor and containerColor2.
    // Gradients are emitted into <defs> and referenced via url(#id).
    const cUid = Math.random().toString(36).slice(2, 9);
    let containerPaintAttr = `fill="${containerColor}"`;
    let containerOpacityAttr = containerOpacity < 1 ? ` fill-opacity="${containerOpacity}"` : "";
    let containerGradientDef = "";
    if (containerGradient === "linear") {
      const gid = `lp-cont-grad-${cUid}`;
      containerGradientDef =
        `<linearGradient id="${gid}" gradientUnits="objectBoundingBox" x1="0" y1="0" x2="1" y2="0" gradientTransform="rotate(${containerGradientAngle} 0.5 0.5)">` +
        `<stop offset="0%" stop-color="${containerColor}" stop-opacity="${containerOpacity}"/>` +
        `<stop offset="100%" stop-color="${containerColor2}" stop-opacity="${containerOpacity}"/>` +
        `</linearGradient>`;
      containerPaintAttr = `fill="url(#${gid})"`;
      containerOpacityAttr = ""; // opacity baked into the stops
    } else if (containerGradient === "radial") {
      const gid = `lp-cont-grad-${cUid}`;
      containerGradientDef =
        `<radialGradient id="${gid}" gradientUnits="objectBoundingBox" cx="0.5" cy="0.5" r="0.55">` +
        `<stop offset="0%" stop-color="${containerColor}" stop-opacity="${containerOpacity}"/>` +
        `<stop offset="100%" stop-color="${containerColor2}" stop-opacity="${containerOpacity}"/>` +
        `</radialGradient>`;
      containerPaintAttr = `fill="url(#${gid})"`;
      containerOpacityAttr = "";
    }

    if (containerPattern === "none") {
      containerDefs = containerGradientDef;
      containerSvg = `<g ${containerPaintAttr}${containerOpacityAttr}${fillRule}>${shapeSvg}</g>`;
    } else {
      const cClipId = `lp-cont-clip-${cUid}`;
      containerDefs = containerGradientDef + `<clipPath id="${cClipId}"${fillRule}>${shapeSvg}</clipPath>`;
      const patternShapes = buildIconPatternShapes(
        containerPattern,
        { x: cX, y: cY, w: cSize, h: cSize },
        containerPatternColor,
        s.containerPatternDensity ?? 1
      );
      containerSvg = `<g clip-path="url(#${cClipId})">
        <rect x="${cX}" y="${cY}" width="${cSize}" height="${cSize}" ${containerPaintAttr}${containerOpacityAttr}/>
        ${patternShapes}
      </g>`;
    }
  }

  const iconPattern: IconPattern = s.iconPattern ?? "none";
  // Pattern color defaults to background (AT&T-style cutout look) if user hasn't picked one.
  const iconPatternColor = s.iconPatternColor ?? (isTransparent ? "#ffffff" : bg);
  const iconUid = Math.random().toString(36).slice(2, 9);
  const iconTransform = `translate(${iconX},${iconY}) scale(${iconSize / Math.max(iconVB.w, iconVB.h)}) translate(${-iconVB.x},${-iconVB.y})`;
  // Per-path attribute for icons (like face icons) that need even-odd so eyes/mouth punch through.
  const iconRuleAttr = iconFillRule ? ` fill-rule="${iconFillRule}" clip-rule="${iconFillRule}"` : "";
  // Render each path with its own fill (multi-color faces) or inherit iconColor (single-color icons).
  const renderedIconPaths = iconPaths.map((p) => {
    if (typeof p === "string") {
      return `<path d="${p}"${iconRuleAttr}/>`;
    }
    const fill = resolveIconFill(p.fill);
    const ruleAttr = p.fillRule
      ? ` fill-rule="${p.fillRule}" clip-rule="${p.fillRule}"`
      : iconRuleAttr;
    return `<path d="${p.d}" fill="${fill}"${ruleAttr}/>`;
  }).join("");
  // Plain "d" strings only — used as the clip silhouette for icon patterns.
  // For multi-color icons the silhouette is the union of all paths, which gives
  // the head outline + eye holes + mouth holes (the kawaii effect).
  const iconClipPaths = iconPaths.map((p) => {
    const d = typeof p === "string" ? p : p.d;
    const ruleAttr = typeof p === "string"
      ? iconRuleAttr
      : (p.fillRule ? ` fill-rule="${p.fillRule}" clip-rule="${p.fillRule}"` : iconRuleAttr);
    return `<path d="${d}"${ruleAttr}/>`;
  }).join("");

  let iconSvg = "";
  let iconDefs = "";
  if (showIcon) {
    if (iconPngUrl) {
      // Raster cartoon — patterns/colors don't apply. Resolve to absolute URL so
      // the SVG works when loaded via blob: (export rasterization, etc).
      const absoluteHref = iconPngUrl.startsWith("/") && typeof window !== "undefined"
        ? window.location.origin + iconPngUrl
        : iconPngUrl;
      iconSvg = `<g transform="${iconTransform}"><image href="${absoluteHref}" x="${iconVB.x}" y="${iconVB.y}" width="${iconVB.w}" height="${iconVB.h}" preserveAspectRatio="xMidYMid meet"/></g>`;
    } else if (iconPattern === "none") {
      // Multi-color icons need their own per-path fills, so don't set group fill.
      const groupFill = iconIsMultiColor ? "" : ` fill="${iconColor}"`;
      iconSvg = `<g transform="${iconTransform}"${groupFill}>${renderedIconPaths}</g>`;
    } else {
      const clipId = `lp-icon-clip-${iconUid}`;
      iconDefs = `<clipPath id="${clipId}">${iconClipPaths}</clipPath>`;
      const patternShapes = buildIconPatternShapes(iconPattern, iconVB, iconPatternColor, s.iconPatternDensity ?? 1);
      iconSvg = `<g transform="${iconTransform}"><g clip-path="url(#${clipId})">
        <rect x="${iconVB.x}" y="${iconVB.y}" width="${iconVB.w}" height="${iconVB.h}" fill="${iconColor}"/>
        ${patternShapes}
      </g></g>`;
    }
  }

  // Compute max text width per layout so SVG can squeeze long text via lengthAdjust
  let maxTextW = w - 2 * pad;
  if (showIcon && (layout === "icon-left" || layout === "icon-right")) {
    maxTextW = w - iconSize - 2 * pad - 32;
  }
  function fitAttr(text: string, fontSize: number): string {
    if (!text) return "";
    const natural = text.length * fontSize * 0.55;
    if (natural > maxTextW) {
      return ` textLength="${maxTextW}" lengthAdjust="spacingAndGlyphs"`;
    }
    return "";
  }

  const style3D: TitleStyle3D = s.titleStyle3D ?? "flat";
  const depth3D = typeof s.titleDepth3D === "number" ? s.titleDepth3D : 8;
  const stroke3D = typeof s.titleStroke3D === "number" ? s.titleStroke3D : 0;
  const skewDeg = Math.max(-30, Math.min(0, typeof s.titleSkew === "number" ? s.titleSkew : 0));
  const uid = Math.random().toString(36).slice(2, 9);

  const haloColor = pickHaloColor(surface);
  const titleBlock: TitleBlock = showText && s.title
    ? buildTitleBlock({
        text: s.title, anchor: titleAnchor,
        fontFamily: s.titleFont, fontSize: s.titleSize, color: titleColor,
        style3D, depth: depth3D, stroke: stroke3D,
        fitAttr: fitAttr(s.title, s.titleSize), uid,
        haloColor,
      })
    : { defs: "", body: "" };

  const titleGroup = titleBlock.body
    ? `<g transform="translate(${titleX + titleNudgeX} ${titleY + titleNudgeY})${skewDeg !== 0 ? ` skewX(${skewDeg})` : ""}">${titleBlock.body}</g>`
    : "";

  // Slogan: route through buildTitleBlock so it gets the same 3D style options
  // as the title. When sloganStyle3D is "flat" (default), the result is a plain
  // <text> element — same look as before, no regression.
  const sloganStyle3D: TitleStyle3D = s.sloganStyle3D ?? "flat";
  const sloganDepth = typeof s.sloganDepth3D === "number" ? s.sloganDepth3D : 4;
  const sloganStroke = typeof s.sloganStroke3D === "number" ? s.sloganStroke3D : 0;
  const sloganSkewDeg = Math.max(-30, Math.min(0, typeof s.sloganSkew === "number" ? s.sloganSkew : 0));
  const sloganBlock: TitleBlock = showText && s.slogan
    ? buildTitleBlock({
        text: s.slogan, anchor: sloganAnchor,
        fontFamily: s.sloganFont, fontSize: s.sloganSize, color: sloganColor,
        style3D: sloganStyle3D, depth: sloganDepth, stroke: sloganStroke,
        fitAttr: fitAttr(s.slogan, s.sloganSize), uid: `slogan-${uid}`,
        haloColor,
      })
    : { defs: "", body: "" };
  // buildTitleBlock emits font-weight="900" — slogans normally want 400, so for
  // flat style we override the body to match the previous lightweight look.
  const sloganBodyAdjusted = sloganStyle3D === "flat" && sloganBlock.body
    ? sloganBlock.body.replace('font-weight="900"', 'font-weight="400"')
    : sloganBlock.body;
  const sloganSvg = sloganBodyAdjusted
    ? `<g transform="translate(${sloganX + sloganNudgeX} ${sloganY})${sloganSkewDeg !== 0 ? ` skewX(${sloganSkewDeg})` : ""}">${sloganBodyAdjusted}</g>`
    : "";

  const allDefs = `${titleBlock.defs}${sloganBlock.defs}${iconDefs}${containerDefs}`;
  const defsBlock = allDefs ? `<defs>${allDefs}</defs>` : "";

  const bgRect = isTransparent ? "" : `<rect width="${w}" height="${h}" fill="${bg}"/>`;

  // Logo frame — backdrop shape behind the entire composition. Rendered after
  // the canvas bg so transparent canvases still get a visible logo backdrop.
  const logoFrame = s.logoFrame ?? "none";
  const logoFrameColor = s.logoFrameColor ?? "#ffffff";
  // Independent width/height fractions. Fall back to legacy uniform `logoFrameSize`
  // for older drafts that haven't been re-saved since the split.
  const legacySize = s.logoFrameSize ?? 0.85;
  const logoFrameWidthFrac  = Math.max(0.2, Math.min(1, s.logoFrameWidth  ?? legacySize));
  const logoFrameHeightFrac = Math.max(0.2, Math.min(1, s.logoFrameHeight ?? legacySize));
  const fW = w * logoFrameWidthFrac;
  const fH = h * logoFrameHeightFrac;
  const fX = (w - fW) / 2;
  const fY = (h - fH) / 2;
  // Border attrs are applied to whichever shape the frame resolves to. Stroke is
  // centered on the path edge, so we inset by sw/2 to keep the full stroke inside
  // the canvas instead of getting clipped at the bounds.
  const frameBorderColor = s.logoFrameBorderColor ?? "#0c2572";
  const frameBorderWidth = Math.max(0, Math.min(30, s.logoFrameBorderWidth ?? 0));
  const frameBorderStyle = s.logoFrameBorderStyle ?? "solid";
  const sw = frameBorderWidth;
  const inset = sw / 2;
  const dashAttr =
    sw > 0 && frameBorderStyle === "dashed" ? ` stroke-dasharray="${sw * 3} ${sw * 2}"`
    : sw > 0 && frameBorderStyle === "dotted" ? ` stroke-dasharray="0 ${sw * 2}" stroke-linecap="round"`
    : "";
  const strokeAttrs = sw > 0
    ? ` stroke="${frameBorderColor}" stroke-width="${sw}"${dashAttr}`
    : "";
  let frameSvg = "";
  if (logoFrame === "circle") {
    // Use ellipse so stretching one axis turns the circle into a proper oval
    // instead of locking to the smaller dimension.
    const rx = fW / 2 - inset;
    const ry = fH / 2 - inset;
    frameSvg = `<ellipse cx="${w / 2}" cy="${h / 2}" rx="${rx}" ry="${ry}" fill="${logoFrameColor}"${strokeAttrs}/>`;
  } else if (logoFrame === "square") {
    frameSvg = `<rect x="${fX + inset}" y="${fY + inset}" width="${fW - sw}" height="${fH - sw}" fill="${logoFrameColor}"${strokeAttrs}/>`;
  } else if (logoFrame === "rounded") {
    const r = Math.min(fW, fH) * 0.14;
    frameSvg = `<rect x="${fX + inset}" y="${fY + inset}" width="${fW - sw}" height="${fH - sw}" rx="${r}" ry="${r}" fill="${logoFrameColor}"${strokeAttrs}/>`;
  } else if (logoFrame === "pill" || logoFrame === "rectangle" || logoFrame === "rectangle-rounded") {
    // Horizontal frames — width/height fully controlled by the user. Pill default
    // height (0.85) makes it square; user typically pulls Frame height down to
    // get the classic banner look. Pill keeps its full-radius ends regardless.
    let radius = 0;
    if (logoFrame === "pill")                   radius = Math.min(fW, fH) / 2;
    else if (logoFrame === "rectangle-rounded") radius = Math.min(fW, fH) * 0.12;
    const radAttr = radius > 0 ? ` rx="${radius}" ry="${radius}"` : "";
    frameSvg = `<rect x="${fX + inset}" y="${fY + inset}" width="${fW - sw}" height="${fH - sw}"${radAttr} fill="${logoFrameColor}"${strokeAttrs}/>`;
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}">
    ${defsBlock}
    ${bgRect}
    ${frameSvg}
    ${containerSvg}
    ${iconSvg}
    ${titleGroup}
    ${sloganSvg}
  </svg>`;
}
